import ReactEmojiPicker, { EmojiClickData, EmojiStyle } from 'emoji-picker-react';
import { useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { DEFAULT_MARGIN, EMOJI_PICKER_HEIGHT, Z_INDEX } from '@constants/layout';
import { Layout } from '@design-system';
import { useDetectOutsideClick } from '@hooks/useDetectOutsideClick';
import { useBoundStore } from '@stores/useBoundStore';
import { getUnifiedEmoji } from '@utils/emojiHelpers';
import { EMOJI_CATEGORIES } from './EmojiPicker.constants';
import { EmojiPickerCustomStyle } from './EmojiPicker.styled';

interface EmojiPickerProps {
  postId?: number;
  postType?: 'Note' | 'Response' | 'Comment';
  onSelectEmoji: (emoji: EmojiClickData) => void;
  onUnselectEmoji?: (emoji: EmojiClickData) => void;
  selectedEmojis?: string[];
  height?: number;
  left?: number;
  createPortalId?: string;
}

function EmojiPicker({
  postId,
  postType,
  onSelectEmoji,
  selectedEmojis,
  height = EMOJI_PICKER_HEIGHT.DEFAULT,
  left = DEFAULT_MARGIN,
  onUnselectEmoji,
  createPortalId,
}: EmojiPickerProps) {
  const { emojiPickerTarget, setEmojiPickerTarget } = useBoundStore((state) => ({
    emojiPickerTarget: state.emojiPickerTarget,
    setEmojiPickerTarget: state.setEmojiPickerTarget,
  }));

  const handleSelectEmoji = useCallback(
    (emoji: EmojiClickData, e: MouseEvent) => {
      e.stopPropagation();

      const isAlreadySelected = selectedEmojis?.includes(emoji.emoji);
      if (!isAlreadySelected) {
        onSelectEmoji(emoji);
      } else {
        onUnselectEmoji?.(emoji);
      }

      setEmojiPickerTarget(null);
    },
    [onSelectEmoji, onUnselectEmoji, selectedEmojis, setEmojiPickerTarget],
  );

  const isVisible =
    emojiPickerTarget &&
    (emojiPickerTarget.type === 'CheckIn' ||
      (emojiPickerTarget.type === postType && emojiPickerTarget.id === postId));

  const emojiPickerWrapper = useDetectOutsideClick({
    callback: () => {
      setEmojiPickerTarget(null);
    },
    enabled: !!(emojiPickerTarget && emojiPickerTarget.type === 'CheckIn'),
  });

  const content = useMemo(() => {
    if (!isVisible) return null;

    const unifiedEmojiList = selectedEmojis?.map((e) => getUnifiedEmoji(e)) || [];

    return (
      <Layout.Absolute l={left} t={emojiPickerTarget.top} z={Z_INDEX.EMOJI_PICKER}>
        <Layout.LayoutBase ref={emojiPickerWrapper} onClick={(e) => e.stopPropagation()}>
          {selectedEmojis && <EmojiPickerCustomStyle unifiedList={unifiedEmojiList} />}
          <ReactEmojiPicker
            height={height}
            emojiStyle={EmojiStyle.NATIVE}
            onEmojiClick={handleSelectEmoji}
            autoFocusSearch={false}
            skinTonesDisabled
            previewConfig={{
              showPreview: false,
            }}
            categories={EMOJI_CATEGORIES}
            lazyLoadEmojis
          />
        </Layout.LayoutBase>
      </Layout.Absolute>
    );
  }, [
    emojiPickerTarget,
    emojiPickerWrapper,
    handleSelectEmoji,
    height,
    isVisible,
    left,
    selectedEmojis,
  ]);

  const element = createPortalId ? document.getElementById(createPortalId) : null;

  if (createPortalId && element) {
    return createPortal(content, element);
  }
  return content;
}
export default EmojiPicker;
